import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "./components/LoginPage";
import AdminDashboard from "./components/AdminDashboard";
import UserHome from "./components/UserHome";
import UserDashboard from "./components/UserDashboard";
import InvestorReport from "./components/InvestorReport";
import ContainerBoard from "./components/ContainerBoard";
import ContainerDetail from "./components/ContainerDetail";
import InvestorPortfolio from "./components/InvestorPortfolio";
import DeveloperDashboard from "./components/DeveloperDashboard";
import DeveloperAccounts from "./components/DeveloperAccounts";
import DeveloperBrowse from "./components/DeveloperBrowse";
import DeveloperContainers from "./components/DeveloperContainers";
import ProtectedRoute from "./components/ProtectedRoute";

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LoginPage />} />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* User Routes */}
        <Route
          path="/user"
          element={
            <ProtectedRoute requiredRole="user">
              <UserHome />
            </ProtectedRoute>
          }
        >
          <Route
            path="dashboard"
            element={
              <ProtectedRoute requiredRole="user">
                <UserDashboard />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Developer Routes — a THIRD role, separate from admin in both
            directions. requiredRole="developer" is exact equality in
            ProtectedRoute, so an admin token is turned away here just as a
            developer token is turned away from /admin above. The server side of
            that same split is middleware/isDeveloper.js.

            /dev on its own redirects rather than falling through to the
            catch-all: the catch-all sends everything unknown to "/", so typing
            /dev would land a signed-in developer back on the login screen and
            read as a broken login. */}
        <Route path="/dev" element={<Navigate to="/dev/dashboard" replace />} />
        <Route
          path="/dev/dashboard"
          element={
            <ProtectedRoute requiredRole="developer">
              <DeveloperDashboard />
            </ProtectedRoute>
          }
        />

        {/* The detail behind a dashboard tile. One route for all seven kinds:
            the kind is a path segment and everything that narrows the list —
            page, search, sort, direction, rows per page — is a query parameter,
            so any particular view is a link someone can be sent. */}
        {/* Container management, on its own page. It is configuration, not
            history, and sharing the dashboard meant the first thing you saw on
            the way to a download was a grid of forms. */}
        <Route
          path="/dev/containers"
          element={
            <ProtectedRoute requiredRole="developer">
              <DeveloperContainers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dev/browse/:kind"
          element={
            <ProtectedRoute requiredRole="developer">
              <DeveloperBrowse />
            </ProtectedRoute>
          }
        />

        {/* Account configuration. Same guard, its own page: it holds broker
            credentials, so it is developer-only like everything under /dev. */}
        <Route
          path="/dev/accounts"
          element={
            <ProtectedRoute requiredRole="developer">
              <DeveloperAccounts />
            </ProtectedRoute>
          }
        />

        {/* Investor Report — admin only. Standalone page (has its own header).
            NOTE: ProtectedRoute is a client-side guard, so it hides the UI but is
            not a security boundary — the bundle is public either way. */}
        {/* PUBLIC by request: reachable without signing in, so it can be shared
            with investors who have no account. The API routes behind it are
            unauthenticated to match — see routes/investorReportRoutes.js.
            Anyone with this URL can read live balances, positions and trade
            history for every configured account. */}
        <Route path="/investor-report" element={<InvestorReport />} />

        {/* THE SAME REPORT, LISTING EVERY CONTAINER.
            One difference from the route above and no other: the dropdown offers
            every book, including ones marked not public and ones disabled, where
            /investor-report offers a visitor only the public enabled ones.

            PUBLIC, requested explicitly and knowingly. It is worth writing down
            what that means, because the flag it overrides exists to prevent it:
            anyone with this URL reads live balances, positions, trade history and
            settlements for EVERY container, `publicVisible: false` included.
            containerScope's own header says a private container "must not be
            reachable by guessing its id in the URL either" - that guarantee holds
            on /investor-report and does not hold here.

            It reads /api/investor-report/all-containers, a separate mount of the
            same handler, rather than sending a flag to the ordinary endpoint - so
            the public report cannot be talked into the same listing by a query
            parameter, and this path is the only way to it. */}
        <Route path="/all/investment/reports" element={<InvestorReport allContainers />} />

        {/* TWO CONDENSED BOARDS, their own components rather than modes of the
            report above. InvestorReport.js was left alone deliberately:
            /investor-report, /investment-portfolio and -v1 all render through it
            and were asked to keep behaving exactly as they do, and the surest
            way to keep that promise is not to edit the file. The cost is that
            reportCommon.js holds a copy of the formatters and that the live
            positions and trade-log tables are duplicated in ContainerDetail —
            copied character for character, not reimplemented, so they cannot
            look or behave differently.

            BOTH ARE PUBLIC AND BOTH LIST EVERY CONTAINER, private and disabled
            included, exactly as /all/investment/reports does and for the same
            explicit request. The same warning applies: anyone with either URL
            reads live figures for every book, `publicVisible: false` included. */}

        {/* Every container side by side, no selector. Four figures per book and
            five per broker: the 02:00-02:00 session P&L and the total since that
            book's own period start, each with its trade count, plus floating and
            per-leg lots. */}
        <Route path="/all/investment/overview" element={<ContainerBoard />} />

        {/* One container at a time, chosen from a dropdown that offers no "All".
            The report's own live positions and trade log, without the capital
            flows, the period statistics or the settlement reconciliation. */}
        <Route path="/all/investment/detail" element={<ContainerDetail />} />

        {/* THE SAME PAGE, PUBLIC-FILTERED. One difference from the route above
            and no other: its dropdown offers only the enabled, publicVisible
            containers, because it reads the ordinary /api/investor-report rather
            than the all-containers mount — so `visibleTo` comes from the
            request instead of being forced to "developer".

            Deliberately NOT under /all/investment/, which in this codebase now
            means "lists non-public books". This one does not, which is the whole
            point of it existing.

            Same component, so the figures, the positions and the trade log
            cannot drift from the page above; only the list differs. Note that a
            signed-in DEVELOPER is still offered private containers here, exactly
            as on /investor-report — an anonymous visitor, who this page is for,
            is not. */}
        <Route path="/investment/detail" element={<ContainerDetail allContainers={false} />} />

        {/* ------------------------- THE FUND VIEW -------------------------
            The same two pages above, for ONE container, restated on a fixed
            capital base: each account's opening capital is stated as 100,000
            and every percentage is measured against it. Trades, P&L, the trade
            log and the positions are the real ones, untouched.

            Both read /api/investor-report/fund, which chooses the container in
            code and ignores `?container=`. So these pages have no selector and
            cannot be pointed at another book from the URL — the capital base
            was chosen for this one, and stating another container's figures on
            it would be the worst thing this route could do.

            THE SAME TWO COMPONENTS, through the `endpoint` prop that
            /investment-portfolio-v1 already uses. Not copies: the figures, the
            positions and the trade log cannot drift from the pages they
            mirror, because they are the same code.

            PUBLIC, like both originals. Note that this URL and
            /investor-report describe the SAME container and will disagree
            about capital and about every percentage — that is what the page is
            for, and it is written here so it is not read as a fault. */}
        <Route
          path="/fund/investor-report"
          element={<InvestorReport endpoint="/api/investor-report/fund" />}
        />
        <Route
          path="/fund/investment/detail"
          element={<ContainerDetail endpoint="/api/investor-report/fund" />}
        />

        {/* The sheet-driven portfolio. Its own component and its own stylesheet,
            so the report above and the frozen backup below - which share
            InvestorReport.js - cannot be affected by anything done here.

            PUBLIC, like the report above. Every money figure on it comes from the
            control sheet (see docs/sheet-guide.md); the real accounts supply only
            the shape of the trade log. The payload is stripped before it leaves
            the server, so nothing on the wire says where the figures came from -
            see mt5/sheetReport.js. */}
        <Route path="/investment-portfolio" element={<InvestorPortfolio />} />

        {/* FROZEN BACKUP of what /investment-portfolio served before the route
            above replaced it. Investors have already been shown these figures,
            so the engine behind
            it - mt5/normalise.js and its `normalisedrows` / `normalisedfactors`
            collections, including 284 sealed trade rows - must keep producing
            exactly what it produces today.

            That is why the replacement got its OWN module and its OWN
            collections rather than this one being copied into v1 copies. Nothing
            here is duplicated, so nothing here can drift, and no migration had to
            be trusted. The replacement now on /investment-portfolio reads the
            sheet and writes only `sheetrows`; it never imports normalise.js or
            touches its collections. */}
        <Route
          path="/investment-portfolio-v1"
          element={<InvestorReport endpoint="/api/investor-report/normalised" />}
        />

        {/* /investment-portfolio-next used to live here - the path this page was
            built on while /investment-portfolio still served v1. It was kept as
            an alias afterwards, which meant two public URLs serving the same
            live figures for no benefit. Removed; the catch-all sends it home. */}

        {/* Catch-all Route */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
